import { useEffect, useState } from "react";

import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Typography, Flex } from "antd";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import FormField from "../../../components/form/FormField.jsx";
import { HookFormRichTextInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import { DEFAULT_RICH_TEXT_TOOLBAR } from "@/components_v2/FormFields/Input/constants";
import { useLazyGetUsersQuery } from "../../../redux/service.js";
import SurveySelectModal from "./SurveySelectModal.jsx";
import { Capabilities, SharingRights } from "@/shared/utils/constants";
import { hasCapability } from "@/shared/utils/roles";
import BaseModal from "../../../components_v2/Modal/BaseModal.tsx";
import BaseButton from "../../../components_v2/Button/BaseButton.js";
import EvidenceTable from "@/domains/evidence/components/EvidenceTable.tsx";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";

import "./styles.css";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants.js";

const { Title } = Typography;

const ContentField = ({
  form,
  children,
  label = null,
  allowAttachments = true,
}) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const canAccessEvidence = hasCapability(
    currentUser,
    Capabilities.ACCESS_EVIDENCE,
  );
  const [getUsers] = useLazyGetUsersQuery();
  const [surveySelectModalOpen, setSurveySelectModalOpen] = useState(false);
  const [blocksModalOpen, setDataBlocksModalOpen] = useState(false);
  const [selectedBlocks, setSelectedBlocks] = useState([]);

  const attachedBlocks = form.watch("blocks") || [];
  const attachedSurveyViews = form.watch("surveyViews") || [];

  useEffect(() => {
    const values = form.getValues();
    if (values?.blocks) {
      setSelectedBlocks(values?.blocks);
    }
  }, [form]);

  const removeBlock = (blockId) => {
    form.setValue(
      "blocks",
      attachedBlocks.filter((i) => i.id !== blockId),
    );
  };

  const removeSurveyView = (surveyViewID) => {
    form.setValue(
      "surveyViews",
      attachedSurveyViews.filter((i) => i.id !== surveyViewID),
    );
  };

  const handleAddSelectedBlocks = () => {
    form.setValue("blocks", selectedBlocks);
    setDataBlocksModalOpen(false);
  };

  return (
    <div>
      <FormField
        label={label}
        required
        field={
          <>
            <HookFormRichTextInput
              name="content"
              media={{ useMediaUpload: true }}
              modules={{
                toolbar: DEFAULT_RICH_TEXT_TOOLBAR,
                mention: {
                  mentionDenotationChars: ["@"],
                  source: async function (searchTerm, renderList) {
                    const response = await getUsers({
                      search: searchTerm || undefined,
                      page: 1,
                      pageSize: 20,
                    });

                    const values = response.isSuccess
                      ? response.data.results.map((i) => ({
                          id: i.id,
                          value: i.fullName,
                        }))
                      : [];

                    renderList(values, searchTerm);
                  },
                },
              }}
              control={form.control}
            />
            {attachedBlocks.length || attachedSurveyViews.length ? (
              <div className="mt-3">
                <div className="mb-3">
                  {attachedBlocks.map((block) => (
                    <div
                      key={block.id}
                      className="py-2 px-3 mb-2"
                      style={{
                        backgroundColor: "rgba(242, 242, 245, 1)",
                        display: "flex",
                      }}
                    >
                      <CloseOutlined onClick={() => removeBlock(block.id)} />
                      <Link
                        to={`/evidence/${block.id}`}
                        target="_blank"
                        className="ms-3"
                      >
                        <Title
                          level={5}
                          style={{ color: "rgba(74, 105, 156, 1)" }}
                        >
                          {
                            block.fieldValues.find(
                              (value) =>
                                value.field === block.fieldsMapping.title,
                            )?.value
                          }
                        </Title>
                      </Link>
                    </div>
                  ))}
                </div>
                <div>
                  {attachedSurveyViews.map((surveyView) => (
                    <div
                      key={surveyView.id}
                      className="py-2 px-3 mb-2"
                      style={{
                        backgroundColor: "rgba(230, 237, 254, 1)",
                        display: "flex",
                      }}
                    >
                      <CloseOutlined
                        onClick={() => removeSurveyView(surveyView.id)}
                      />
                      <Title
                        level={5}
                        className="ms-3"
                        style={{ color: "rgba(74, 105, 156, 1)" }}
                      >
                        {surveyView.title}
                      </Title>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {allowAttachments && (
              <Flex wrap gap={4}>
                {canAccessEvidence && (
                  <BaseButton
                    variant={BUTTON_VARIANTS.LINK}
                    leadingIcon={<PlusOutlined />}
                    onClick={() => setDataBlocksModalOpen(true)}
                  >
                    {t("addEvidence")}
                  </BaseButton>
                )}

                <BaseButton
                  variant={BUTTON_VARIANTS.LINK}
                  leadingIcon={<PlusOutlined />}
                  onClick={() => setSurveySelectModalOpen(true)}
                >
                  {t("addReportView")}
                </BaseButton>
              </Flex>
            )}

            <div>{children}</div>
          </>
        }
      />
      <BaseModal
        className="evidence-selection-modal"
        title={t("labels.selectEvidence")}
        open={blocksModalOpen}
        onCancel={() => setDataBlocksModalOpen(false)}
        footer={
          <Flex wrap justify="space-between" align="center" gap={"small"}>
            <BaseButton
              variant={BUTTON_VARIANTS.TERTIARY}
              key="cancel"
              onClick={() => setDataBlocksModalOpen(false)}
            >
              {t("buttons.cancel")}
            </BaseButton>

            <BaseButton
              variant={BUTTON_VARIANTS.PRIMARY}
              key="confirm"
              onClick={handleAddSelectedBlocks}
            >
              {t("buttons.confirm")}
            </BaseButton>
          </Flex>
        }
      >
        {currentUser && (
          <EvidenceTable
            columnNames={["title", "type", "sharingRights"]}
            filterNames={["type", "search"]}
            defaultFilters={{
              editable: true,
              organizations: [currentUser.organization?.id],
            }}
            rowSelection={{
              enabled: true,
              multiple: true,
              selectedRows: selectedBlocks,
              onSelect: (blocks) => setSelectedBlocks(blocks),
            }}
          />
        )}
      </BaseModal>

      <SurveySelectModal
        onCancel={() => setSurveySelectModalOpen(false)}
        open={surveySelectModalOpen}
        initialSelectedSurveys={attachedSurveyViews}
        multiple
        onSelect={(surveyViews) => {
          form.setValue("surveyViews", surveyViews);
          setSurveySelectModalOpen(false);
        }}
        filters={{
          sharing_rights: [
            SharingRights.PUBLIC.value,
            SharingRights.PRME_COMMUNITY.value,
          ],
        }}
      />
    </div>
  );
};

ContentField.propTypes = {
  form: PropTypes.object.isRequired,
  children: PropTypes.element.isRequired,
  label: PropTypes.string,
  allowAttachments: PropTypes.bool,
};

export default ContentField;
